import http from 'config/http'
import {
  LIST_GITHUB_REPOS,
  STORE_GITHUB_TOKEN,
  UPDATE_USER_SESSION,
  UPDATE_SUBSCRIPTION_INFO,
  AUTHORIZED_CHAT,
  AUTH_INITIALIZED,
} from '../types/auth.type'
import { privatePages } from 'utils/constants'
import { supabase } from 'config/supabase'
import { notifyToast } from 'utils/responseUtils'
import { isBrowser } from 'utils/isBrowser'
import { setOneSignalExternalUserId, removeOneSignalExternalUserId } from 'utils/oneSignalUtils'

export const authHandler = () => async (dispatch) => {
  try {
    // Quick localStorage check first to potentially restore session faster
    const storedSession = isBrowser() ? localStorage.getItem('session') : null

    const {
      data: { session },
      error,
    } = await supabase.auth.getSession()

    if (error) {
      await handleLogout()
      dispatch({ type: AUTH_INITIALIZED })
      return
    }

    if (session) {
      // If we have both stored session and supabase session, user is authenticated
      dispatch(getExtraUserData(session.access_token, session.user))
      dispatch(updateUserSession(session))
      if (session.expires_in < 3600) refreshTokenHandler()
    } else {
      // Clear any stale session data
      if (storedSession) {
        localStorage.removeItem('session')
      }
      dispatch({ type: AUTH_INITIALIZED })
    }
  } catch (error) {
    console.error('Auth initialization error:', error)
    // Clear potentially corrupted session data
    if (isBrowser()) {
      localStorage.removeItem('session')
    }
    dispatch({ type: AUTH_INITIALIZED })
  }
}

export const refreshTokenHandler = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.refreshSession()
  if (error) await handleLogout()
  if (session) {
    await localStorage.setItem('session', session.access_token)
  }
}

export const updateUserSession = (session) => async (dispatch) => {
  const user = session?.user
  dispatch({
    type: UPDATE_USER_SESSION,
    payload: {
      user: user
        ? {
            id: user.id,
            email: user.email,
            phone: user.phone || user.new_phone,
            full_name: user.user_metadata.full_name || '',
            avatar_url: user.user_metadata.avatar_url,
            expiresAt: session.expires_at,
          }
        : null,
      isAuthenticated: user ? true : false,
    },
  })

  // OneSignal external user ID management
  if (user?.id) {
    // User is logging in - set external user ID
    await setOneSignalExternalUserId(user.id)
  } else {
    // User is logging out - remove external user ID
    await removeOneSignalExternalUserId()
  }

  if (user && session?.access_token) await localStorage.setItem('session', session.access_token)
}

export const updateUserInfo = (data) => async (dispatch) => {
  const { data: userInfo, error } = await supabase.auth.updateUser({ ...data })
  if (error?.message) {
    dispatch(notifyToast('error', error.message))
  } else {
    dispatch(notifyToast('success', 'User Information is updated successfully!'))
    dispatch(updateUserSession(userInfo))
  }
}

export const verifyOtp = (phone, token) => async (dispatch) => {
  try {
    const resp = await fetch('/api/supabase/verify-otp', {
      method: 'POST',
      body: JSON.stringify({
        phone,
        token,
      }),
    })
    const response = await resp.json()
    return response
  } catch (error) {
    dispatch(notifyToast('error', error?.message || error))
    return { success: false }
  }
}

export const getExtraUserData = (token, user) => async (dispatch) => {
  const response = await fetch(`/api/supabase/fetch-user-data?userId=${user.id}&nocache=${new Date().getTime()}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const parseRes = await response.json()
  if (!user?.user_metadata?.extension && parseRes.extension) {
    await supabase.auth.updateUser({ data: { extension: parseRes.extension } })
  }

  dispatch({ type: UPDATE_SUBSCRIPTION_INFO, payload: parseRes })
}

export const handleLogout = (pathname, router) => async (dispatch) => {
  await localStorage.clear()
  await supabase.auth.signOut()
  if (privatePages.includes(pathname)) {
    router.push({
      pathname: '/',
    })
  }
  dispatch(updateUserSession(null))
}

export const storeGithubTokenAction = (token) => async (dispatch) => {
  dispatch({ type: STORE_GITHUB_TOKEN, payload: token })
}

export const listGithubReposAction = (githubToken, callback) => async (dispatch) => {
  const token = localStorage.getItem('session')
  try {
    const payload = await http
      .get(`${process.env.NEXT_PUBLIC_BASE_URL}/api/github/list-repo?githubToken=${githubToken}&token=${token}`)
      .then((res) => res.data)

    dispatch({ type: LIST_GITHUB_REPOS, payload })
    callback()
  } catch (e) {
    callback()
  }
}

export const buildGithubModelAction = async (githubToken, data, callback) => {
  const token = localStorage.getItem('session')
  try {
    const payload = await http
      .post(`${process.env.NEXT_PUBLIC_BASE_URL}/api/github/build-repo?githubToken=${githubToken}&token=${token}`, data)
      .then((res) => res.data)
    return payload
  } catch (err) {
    callback(err?.response?.data?.error)
  }
}

export const createConfigAction = async (githubToken, data, callback) => {
  const token = localStorage.getItem('session')
  try {
    const payload = await http
      .post(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/github/create-config?githubToken=${githubToken}&token=${token}`,
        data
      )
      .then((res) => res.data)
    return payload
  } catch (err) {
    callback(err?.response?.data?.error)
  }
}

export const createTokenAction = async (data, callback) => {
  const token = localStorage.getItem('session')
  return http
    .post('tokens', data, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => callback(res.data))
}

export const updateTokenAction = async (id, data, callback) => {
  const token = localStorage.getItem('session')
  return http
    .put(`tokens/name/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => callback(res.data))
}

export const deleteTokenAction = async (id, callback) => {
  const token = localStorage.getItem('session')
  try {
    return http
      .delete(`tokens/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => callback(true))
  } catch (err) {
    callback(false)
  }
}

export const getTokensAction = async (callback) => {
  const token = localStorage.getItem('session')
  return http
    .get('tokens', {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => callback(res.data))
}

export const authenticateChat = (payload) => async (dispatch) => {
  dispatch({
    type: AUTHORIZED_CHAT,
    payload: payload,
  })
}
